import { Button } from '@/components/ui/button';
import { Tag } from '@/components/ui/tag';
import Link from 'next/link';
import { MOTION } from '@/utils';
import { PRICING_FEATURES, PRICING_PLANS } from '@/utils/data/princingFunctions';
import { useGSAP } from '@gsap/react';
import { gsap } from 'gsap';
import { SplitText } from 'gsap/SplitText';
import { useRef } from 'react';

const collaborateurPlan = PRICING_PLANS.find((p) => p.id === 'collaborateur')!;

const CheckIcon = () => (
  <svg
    aria-hidden="true"
    className="text-orange h-4 w-4 shrink-0"
    fill="none"
    viewBox="0 0 16 16"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M3 8.5L6.5 12L13 5"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="1.5"
    />
  </svg>
);

export default function Pricing() {
  const refs = {
    section: useRef(null),
    tag: useRef(null),
    title: useRef(null),
    subtitle: useRef(null),
    card: useRef(null),
    price: useRef(null),
    counter: useRef<HTMLSpanElement>(null),
    units: useRef(null),
    engagement: useRef(null),
    callout: useRef(null),
    cta: useRef(null),
    features: useRef<HTMLDivElement[]>([]),
  };

  const setFeatureRef = (el: HTMLDivElement | null, index: number) => {
    if (el) refs.features.current[index] = el;
  };

  useGSAP(
    () => {
      if (!refs.section.current) return;

      const splitTitle = new SplitText(refs.title.current, {
        mask: 'lines',
        type: 'lines',
      });
      const splitSubtitle = new SplitText(refs.subtitle.current, {
        mask: 'lines',
        type: 'lines',
      });
      const splitEngagement = new SplitText(refs.engagement.current, {
        mask: 'lines',
        type: 'lines',
      });
      const featureEls = refs.features.current.filter(Boolean);
      const counterValue = { value: 0 };

      gsap.set(refs.tag.current, { autoAlpha: 0, y: 8 });

      const tl = gsap.timeline({
        defaults: { duration: MOTION.durationMd, ease: MOTION.ease },
        scrollTrigger: {
          trigger: refs.section.current,
          start: 'top 60%',
          toggleActions: 'play none none reverse',
        },
      });

      tl.to(refs.tag.current, {
        autoAlpha: 1,
        y: 0,
        duration: MOTION.durationSm,
        ease: MOTION.ease,
      })
        .from(
          splitTitle.lines,
          { yPercent: MOTION.yPercent, stagger: MOTION.staggerLines },
          '<+0.15',
        )
        .from(
          splitSubtitle.lines,
          { yPercent: MOTION.yPercent, stagger: MOTION.staggerLines },
          '<+0.1',
        )
        .from(refs.card.current, { autoAlpha: 0, y: MOTION.y }, '<+0.2')
        .from(refs.price.current, { autoAlpha: 0, scale: 0.85, duration: 0.5 }, '-=0.2')
        .to(
          counterValue,
          {
            value: 49,
            duration: 1,
            ease: 'power2.out',
            onUpdate: () => {
              if (refs.counter.current) {
                refs.counter.current.textContent = Math.round(counterValue.value).toString();
              }
            },
          },
          '<',
        )
        .from(refs.units.current, { autoAlpha: 0, x: -8, duration: MOTION.durationSm }, '<+0.2')
        .from(splitEngagement.lines, { yPercent: MOTION.yPercent }, '<')
        .from(refs.callout.current, { autoAlpha: 0, y: 8, duration: MOTION.durationSm }, '<+0.1')
        .from(refs.cta.current, { autoAlpha: 0, y: 8, duration: MOTION.durationSm }, '<+0.1')
        .from(
          featureEls,
          {
            autoAlpha: 0,
            y: 10,
            stagger: 0.03,
            duration: MOTION.durationSm,
          },
          '<+0.15',
        );

      return () => {
        splitTitle.revert();
        splitSubtitle.revert();
        splitEngagement.revert();
      };
    },
    { scope: refs.section },
  );

  return (
    <section
      ref={refs.section}
      className="max-width-blue-section flex flex-col items-center gap-12"
      id="tarif"
    >
      {/* En-tête */}
      <div className="flex w-full flex-col gap-3">
        <Tag ref={refs.tag}>Tout inclus, dès le premier jour</Tag>

        <h2 ref={refs.title} className="w-fit">
          Un tarif unique, transparent et accessible
        </h2>

        <p className="sr-only">
          Avec zLawyer, pas de mauvaise surprise : 1 logiciel, un tarif par utilisateur par mois
          avec toutes les fonctionnalités incluses.
        </p>
        <p ref={refs.subtitle} aria-hidden="true" className="max-w-2xl text-left">
          Avec zLawyer, pas de mauvaise surprise : 1 logiciel, 1 tarif par utilisateur par mois avec
          toutes les fonctionnalités incluses.
        </p>
      </div>

      {/* Card 2 panneaux */}
      <div
        ref={refs.card}
        className="grid w-full max-w-6xl grid-cols-1 overflow-hidden rounded-(--radius-current) bg-white shadow-(--shadow-current) lg:grid-cols-[320px_1fr]"
      >
        {/* Panneau gauche : tarif */}
        <div className="border-dark-blue/[0.07] flex flex-col justify-between gap-8 border-b p-8 lg:border-r lg:border-b-0 lg:p-10">
          <div className="flex flex-col gap-6">
            <div className="flex flex-col gap-1">
              <div ref={refs.price} className="flex items-end gap-0.5">
                <span
                  className="text-orange font-roboto-bold leading-none tabular-nums"
                  style={{ fontSize: 'clamp(52px, 7vw, 72px)' }}
                >
                  <span ref={refs.counter}>0</span>€
                </span>
              </div>

              <div ref={refs.units} className="text-orange font-roboto-medium text-lg leading-snug">
                /mois /utilisateur
              </div>

              <p ref={refs.engagement} className="text-dark-blue/50 mt-1 text-sm">
                Sans engagement
              </p>
            </div>

            {/* Callout collaborateur */}
            <div
              ref={refs.callout}
              className="border-dark-blue/8 bg-light-blue/50 flex flex-col gap-5 rounded-xl border p-4"
            >
              <span className="text-dark-blue/60 text-sm leading-snug">
                Vos collaborateurs bénéficient d&apos;un tarif adapté à leur usage.
              </span>
              <div className="flex items-baseline gap-1.5">
                <span className="text-orange font-roboto-bold text-3xl">
                  {collaborateurPlan.price}€
                </span>
                <span className="text-dark-blue/35 text-sm">/mois /collaborateur</span>
              </div>
              <span className="text-dark-blue/35 text-xs">{collaborateurPlan.description}</span>
              {/* <Link
                href="/tarif"
                className="group text-orange font-roboto-medium mt-1 inline-flex items-center gap-1.5 text-sm"
              >
                <span className="underline-offset-2 group-hover:underline">En savoir plus</span>
                <svg
                  aria-hidden="true"
                  className="h-3.5 w-3.5 transition-transform duration-150 group-hover:translate-x-0.5"
                  fill="none"
                  stroke="currentColor"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="1.5"
                  viewBox="0 0 24 24"
                >
                  <path d="M5 12h14M12 5l7 7-7 7" />
                </svg>
              </Link> */}
            </div>
          </div>

          <div ref={refs.cta} className="flex flex-col gap-2">
            <p className="text-dark-blue/25 text-center text-xs!">
              Sans engagement • Résiliation à tout moment
            </p>
            <Button className="whitespace-nowrap" href="/contact" variant="fill">
              Demandez une démo
            </Button>
          </div>
        </div>

        {/* Panneau droit : fonctionnalités */}
        <div className="p-4 lg:p-10">
          <div className="grid grid-cols-1 gap-x-2 sm:grid-cols-2">
            {PRICING_FEATURES.map((feature, index) => (
              <div
                key={feature.label}
                ref={(el) => setFeatureRef(el, index)}
                className="group hover:bg-orange/5 flex items-center gap-3 rounded-md p-3 transition-colors duration-300"
              >
                <CheckIcon />
                <span className="text-dark-blue/80 text-base leading-snug">{feature.label}</span>
                {feature.inCollaborateur && (
                  <Tag className="ml-auto shrink-0 rounded-full border px-2 py-0.5 text-xs">
                    + Collab
                  </Tag>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
